import { useEffect, useState, useCallback } from "react";
import { Table, BasicTable, StartOrderPayload, OrderItem } from "../types";
import * as api from "../api";
import { showGlobalToast } from "../../../utils/toast";

const readGuid = () => window.localStorage.getItem("restaurantGuid") || "";

export const useTables = () => {
  const [tables, setTables] = useState<BasicTable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTables = useCallback(async () => {
    setLoading(true);
    setError(null);
    const restaurantGuid = readGuid();
    try {
      const data = await api.getAllTables(restaurantGuid);
      setTables(data as BasicTable[]);
    } catch (e) {
      setTables([]);
      setError("Failed to load tables");
      showGlobalToast("Failed to load tables", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const refresh = () => fetchTables();

  const getFullTableDetails = async (tableId: string): Promise<Table | null> => {
    const restaurantGuid = readGuid();
    try {
      const fullTable = await api.getTableDetails(tableId, restaurantGuid);
      return fullTable;
    } catch (e) {
      showGlobalToast("Failed to load table details", "error");
      return null;
    }
  };

  const startOrder = async (payload: StartOrderPayload) => {
    try {
      const restaurantGuid = readGuid();
      const resp = await api.startOrder({ ...payload, restaurantGuid });
      // Refresh basic tables to update availability
      await fetchTables();
      showGlobalToast("Order started successfully", "success");
      return resp;
    } catch (e) {
      showGlobalToast("Failed to start order", "error");
      throw e;
    }
  };

  const addItem = async (tableId: string, item: OrderItem) => {
    try {
      const restaurantGuid = readGuid();
      const resp = await api.addItemToOrder(tableId, item, restaurantGuid);
      showGlobalToast("Item added to order", "success");
      return resp;
    } catch (e) {
      showGlobalToast("Failed to add item", "error");
      throw e;
    }
  };

  const changeQty = async (tableId: string, itemId: string, qty: number) => {
    try {
      const restaurantGuid = readGuid();
      const resp = await api.updateItemQty(tableId, itemId, qty, restaurantGuid);
      return resp;
    } catch (e) {
      showGlobalToast("Failed to update quantity", "error");
      throw e;
    }
  };

  const remove = async (tableId: string, itemId: string) => {
    try {
      const restaurantGuid = readGuid();
      const resp = await api.removeItem(tableId, itemId, restaurantGuid);
      showGlobalToast("Item removed from order", "success");
      return resp;
    } catch (e) {
      showGlobalToast("Failed to remove item", "error");
      throw e;
    }
  };

  const moveTable = async (fromTableId: string, toTableId: string) => {
    try {
      const restaurantGuid = readGuid();
      await api.moveTable(fromTableId, toTableId, restaurantGuid);
      await fetchTables();
      showGlobalToast(`Order moved from ${fromTableId} to ${toTableId}`, "success");
    } catch (e) {
      showGlobalToast("Failed to move table", "error");
      throw e;
    }
  };

  return { tables, loading, error, refresh, startOrder, addItem, changeQty, remove, moveTable, getFullTableDetails };
};

export default useTables;
